import { Theme } from './types';

export const retro8BitTheme: Theme = {
  id: 'retro-8bit',
  name: 'Retro 8-Bit Arcade',
  description: 'Nostalgic 8-bit gaming experience with pixel-perfect aesthetics',

  colors: {
    choice1: '#FF0080', // Hot Pink (NES palette)
    choice2: '#00D9FF', // Cyan
    choice3: '#FFFF00', // Yellow
    choice4: '#00FF41', // Bright Green

    correct: '#00FF41',
    incorrect: '#FF0055',

    background: '#0f0f23',
    cardBackground: '#1a1a2e',
    textPrimary: '#ffffff',
    textSecondary: '#00ffff',
    accent: '#ffff00',

    timerHigh: '#00FF41',
    timerMid: '#FFFF00',
    timerLow: '#FF0080',

    // Text colors for choice buttons (for contrast)
    choice1Text: '#ffffff',
    choice2Text: '#000000',
    choice3Text: '#000000', // Black text on yellow background
    choice4Text: '#000000',

    // Semantic colors
    surface: '#1a1a2e',
    surfaceVariant: '#25253f',
    onSurface: '#ffffff',
    onSurfaceVariant: '#00ffff',
    outline: '#00ffff',
    outlineVariant: 'rgba(0, 255, 255, 0.3)',
    scrim: 'rgba(15, 15, 35, 0.8)',

    // Interactive element colors
    primary: '#FF0080',
    onPrimary: '#ffffff',
    secondary: '#00D9FF',
    onSecondary: '#000000',

    // Status colors
    success: '#00FF41',
    warning: '#FFFF00',
    error: '#FF0055',
    info: '#00D9FF',
  },

  typography: {
    fontFamily: "'Press Start 2P', 'Courier New', monospace",
    questionSize: 'lg',
    answerSize: 'sm',
    scoreSize: 'xs',

    sizes: {
      xs: 'text-xs',
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
      '4xl': 'text-4xl',
    },

    lineHeights: {
      tight: 'leading-tight',
      normal: 'leading-normal',
      relaxed: 'leading-relaxed',
    },

    weights: {
      normal: 'font-normal',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
  },

  animations: {
    buttonHover: 'hover:translate-y-[-2px] transition-transform duration-100',
    buttonPress: 'scale-95 brightness-150',
    correctAnswer: 'animate-pulse',
    wrongAnswer: 'animate-bounce',
    transition: 'transition-all duration-100',
  },

  emojis: {
    choice1: '▲',
    choice2: '■',
    choice3: '●',
    choice4: '♦',
    score: '★',
    timer: '◷',
    correct: '✓',
    incorrect: '✗',
  },

  audio: {
    backgroundMusic: 'https://quizcrunch-prod-play-static-website-962595531541.s3.us-east-2.amazonaws.com/media/8-bit-retro/levelthebeat.mp3',
    enabled: true,
  },

  effects: {
    particles: false,
    glassmorphism: false,
    buttonShadows: true,
    confetti: false,
    screenShake: true,
  },

  layout: {
    buttonShape: 'square',
    buttonArrangement: 'grid',
    spacing: 'compact',
  },

  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem',
    '2xl': '4rem',
  },

  elevation: {
    none: 'shadow-none',
    sm: 'shadow-md',
    md: 'shadow-lg',
    lg: 'shadow-xl',
    xl: 'shadow-2xl',
  },

  borderRadius: {
    sm: '0',      // 0 - Square corners for retro feel
    md: '0',      // 0 - Square corners
    lg: '0.25rem', // 4px - Slightly rounded cards
    full: '9999px',
  },

  customCSS: `
    /* CRT scanline effect */
    .retro-scanlines::before {
      content: '';
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      background: linear-gradient(
        to bottom,
        rgba(255, 255, 255, 0) 50%,
        rgba(0, 0, 0, 0.1) 50%
      );
      background-size: 100% 4px;
      z-index: 1000;
      animation: scanline 8s linear infinite;
    }

    @keyframes scanline {
      0% { background-position: 0 0; }
      100% { background-position: 0 100%; }
    }

    /* Pixel border effect */
    .pixel-border {
      box-shadow:
        0 0 0 2px currentColor,
        0 0 0 4px #000,
        0 0 0 6px currentColor;
      image-rendering: pixelated;
    }

    /* Retro button shadow */
    .retro-shadow {
      box-shadow:
        4px 4px 0 rgba(0, 0, 0, 0.8),
        0 0 20px currentColor;
    }

    /* Blink animation for retro elements */
    @keyframes retro-blink {
      0%, 49% { opacity: 1; }
      50%, 100% { opacity: 0.3; }
    }

    .retro-blink {
      animation: retro-blink 1s ease-in-out infinite;
    }

    /* Responsive font sizes for 8-bit theme on mobile */
    @media (max-width: 640px) {
      .theme-retro-8bit h1 {
        font-size: 1.5rem !important;
      }

      .theme-retro-8bit h2 {
        font-size: 1.25rem !important;
      }

      .theme-retro-8bit .text-3xl {
        font-size: 1rem !important;
      }

      .theme-retro-8bit .text-lg {
        font-size: 0.75rem !important;
        line-height: 1.2 !important;
      }

      .theme-retro-8bit .text-xl {
        font-size: 0.875rem !important;
      }
    }
  `,
};